import { useState, useEffect, useCallback } from 'react';
import { authService, User, AuthResponse } from '../services/authService';

export const useOTPAuth = () => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    // Initialize auth state from localStorage
    const token = localStorage.getItem('accessToken');
    const anonymousToken = localStorage.getItem('anonymousSessionToken');

    if (token || anonymousToken) {
      loadUser();
    } else {
      setLoading(false);
    }
  }, []);

  const loadUser = async () => {
    try {
      const userData = await authService.getCurrentUser();
      setUser(userData);
      setIsAuthenticated(true);
    } catch (error) {
      console.error('Failed to load user:', error);
      // Clear invalid tokens
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('anonymousSessionToken');
      setUser(null);
      setIsAuthenticated(false);
    } finally {
      setLoading(false);
    }
  };

  const requestOTP = useCallback(async (email: string): Promise<AuthResponse> => {
    try {
      const result = await authService.requestOTP(email);
      return result;
    } catch (error: any) {
      console.error('Request OTP error:', error);
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to request OTP'
      };
    }
  }, []);

  const verifyOTP = useCallback(async (email: string, code: string): Promise<AuthResponse> => {
    try {
      const result = await authService.verifyOTP(email, code);

      if (result.success) {
        await loadUser();
      }

      return result;
    } catch (error: any) {
      console.error('Verify OTP error:', error);
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to verify OTP'
      };
    }
  }, []);

  const createAnonymousSession = useCallback(async (): Promise<AuthResponse> => {
    try {
      const result = await authService.createAnonymousSession();

      if (result.success) {
        await loadUser();
      }

      return result;
    } catch (error: any) {
      console.error('Create anonymous session error:', error);
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to create anonymous session'
      };
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await authService.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setUser(null);
      setIsAuthenticated(false);
    }
  }, []);

  const reloadUser = useCallback(async () => {
    await loadUser();
  }, []);

  return {
    user,
    loading,
    isAuthenticated,
    isAnonymous: user?.isAnonymous || false,
    requestOTP,
    verifyOTP,
    createAnonymousSession,
    logout,
    reloadUser
  };
};
